import { useEffect, useRef } from 'react';
import type { AnchorDto, RoleLevel } from '@genoma/shared';

import { Brand } from '../../components/Brand.tsx';
import { Centered } from '../../components/Centered.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { useRiseIn } from '../../lib/use-gsap.ts';
import {
  answeredCount,
  currentPeople,
  currentScreen,
  onLastScreen,
  roleLevelJustChanged,
  screenComplete,
  useSurveyStore,
} from '../../stores/survey.store.ts';
import { AnchorCards } from './AnchorCards.tsx';
import { CommentsScreen } from './CommentsScreen.tsx';
import { DoneScreen } from './DoneScreen.tsx';
import { IntroScreen } from './IntroScreen.tsx';
import { PeopleGrid } from './PeopleGrid.tsx';
import { ScaleSelector } from './ScaleSelector.tsx';

/**
 * The rater questionnaire.
 *
 * One behavior per screen in both formats. What changes is what sits under the
 * anchors: a single large scale for one person, or a grid for up to six.
 */
export const SurveyPage = () => {
  const { t, locale, setLocale } = useI18n();
  const store = useSurveyStore();
  const screenRef = useRef<HTMLDivElement>(null);

  const { survey, status, view, behaviorIndex, groupIndex } = store;
  const behavior = survey?.behaviors[behaviorIndex];
  const people = currentPeople(store);

  useEffect(() => {
    void store.load();
    // Loading once on mount is the whole intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The API picks the rater's language from their own preference, and the
  // anchors arrive in it. The interface has to follow, or a rater reads
  // Spanish anchors under English labels.
  useEffect(() => {
    if (survey && survey.locale !== locale) setLocale(survey.locale);
  }, [survey, locale, setLocale]);

  // Anything unsaved must reach the API before the tab goes away.
  useEffect(() => {
    const flushNow = () => void store.flush();
    window.addEventListener('pagehide', flushNow);
    return () => window.removeEventListener('pagehide', flushNow);
  }, [store]);

  useRiseIn(screenRef, '[data-rise]', [behaviorIndex, groupIndex, view]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [behaviorIndex, groupIndex]);

  if (status === 'loading') return <Centered>{t('common.loading')}</Centered>;
  if (status === 'error') {
    return (
      <Centered>
        {store.errorMessage ?? t('errors.generic')}
      </Centered>
    );
  }
  if (!survey) return <Centered>{t('errors.notFound')}</Centered>;

  if (view === 'done' || status === 'submitted') {
    return <DoneScreen />;
  }

  if (view === 'intro') return <IntroScreen />;
  if (view === 'comments') return <CommentsScreen />;
  if (!behavior) return <Centered>{t('errors.generic')}</Centered>;

  const anchors = anchorsFor(survey, behavior.id, people);
  const screen = currentScreen(store);
  const anchorsChanged = roleLevelJustChanged(store);
  const isLastScreen = onLastScreen(store);
  const isLastGroupOfBehavior =
    survey.format === 'INDIVIDUAL' || groupIndex === survey.screens.length - 1;

  const soleAnswer =
    survey.format === 'INDIVIDUAL' && people[0]
      ? store.valueFor(people[0].id, behavior.id)
      : undefined;

  return (
    <div className="mx-auto flex min-h-full max-w-2xl flex-col px-5">
      <Brand organization={survey.brand.organization} program={survey.brand.program} />

      <main ref={screenRef} className="flex-1 pt-1 pb-6">
        <div className="sticky top-0 z-10 -mx-0.5 bg-sand/90 px-0.5 py-3 backdrop-blur-md">
          <div className="h-[5px] overflow-hidden rounded-md bg-line-soft">
            <span
              className="block h-full rounded-md bg-gradient-to-r from-teal to-teal-bright transition-[width] duration-500"
              style={{
                width: `${(answeredCount(store) / (survey.evaluated.length * survey.behaviors.length)) * 100}%`,
              }}
            />
          </div>
          <div className="mt-1.5 flex justify-between font-mono text-[10.5px] tracking-[0.05em] text-faint uppercase">
            <span>
              {t(`pillars.${behavior.pillar}`)} · {t(`pillars.${behavior.pillar}_motto`)}
            </span>
            <span>{t('survey.progress', { current: behaviorIndex + 1, total: survey.behaviors.length })}</span>
          </div>
        </div>

        <div data-rise>
          {/* Finishing was blocked by a gap somewhere earlier. Rather than a
              number in an error, the rater is standing on the gap. */}
          {store.missingAnswers !== null && (
            <div className="mt-3.5 rounded-xl border border-[#E0B4A6] bg-[#FDF0EA] px-3.5 py-3">
              <p className="font-mono text-[10px] font-bold tracking-[0.12em] text-[#8A4A2F] uppercase">
                {t('survey.missingTitle', { count: store.missingAnswers })}
              </p>
              <p className="mt-1 text-[13px] text-[#6B3A24]">{t('survey.missingBody')}</p>
            </div>
          )}

          {/* The anchors are written per role level, so crossing from one
              cohort to the next silently changes what the words describe.
              Saying so is cheaper than a rater answering the wrong scale. */}
          {anchorsChanged && screen && (
            <div className="mt-3.5 rounded-xl border border-[#E3C88A] bg-[#FDF6E6] px-3.5 py-3">
              <p className="font-mono text-[10px] font-bold tracking-[0.12em] text-[#8A6A1F] uppercase">
                {t('survey.anchorsChangedTitle')}
              </p>
              <p className="mt-1 text-[13px] text-[#6B5518]">
                {t('survey.anchorsChangedBody', {
                  level: t(`roleLevels.${screen.roleLevel}`),
                })}
              </p>
            </div>
          )}

          <span className="mt-3.5 inline-flex rounded-[20px] border border-tint-line bg-tint px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.14em] text-teal-deep uppercase">
            {t(`pillars.${behavior.pillar}`)}
          </span>

          <div className="mt-3.5 mb-1">
            <h1 className="text-2xl leading-[1.14] font-extrabold tracking-[-0.02em]">
              <span className="mr-1.5 font-mono text-[15px] font-bold text-teal">
                {String(behavior.number).padStart(2, '0')}
              </span>
              {behavior.name}
            </h1>
            <p className="mt-1.5 max-w-[60ch] text-sm text-soft">{behavior.description}</p>
          </div>

          <AnchorCards
            anchors={anchors}
            selected={typeof soleAnswer === 'number' ? soleAnswer : undefined}
            flash={store.lastMark}
          />

          {/* Naming the group is what stops a rater from carrying the previous
              screen's mental model into this one. */}
          {screen && survey.kind !== 'SELF' && (
            <p
              data-rise
              className="mt-4 mb-1 font-mono text-[10px] tracking-[0.13em] text-teal-deep uppercase"
            >
              {t('survey.groupHeading', {
                group: t(`evaluatingGroup.${screen.relationship}`),
                level: t(`roleLevels.${screen.roleLevel}`),
              })}
            </p>
          )}

          {survey.format === 'INDIVIDUAL' && people[0] ? (
            <ScaleSelector
              value={soleAnswer}
              personName={people[0].fullName}
              isSelf={survey.kind === 'SELF'}
              onChange={(value) => store.answer(people[0]!.id, behavior.id, value)}
            />
          ) : (
            <PeopleGrid
              people={people}
              valueFor={(personId) => store.valueFor(personId, behavior.id)}
              onChange={(personId, value) => store.answer(personId, behavior.id, value)}
            />
          )}

          {store.saveState === 'error' && (
            <p className="mt-3 text-center text-[13px] text-red-700">
              {/* The real reason, not a guess. Telling someone they are offline
                  when the API rejected their submission is worse than silence. */}
              {store.errorMessage ?? t('errors.offline')}
            </p>
          )}
        </div>
      </main>

      <div className="sticky bottom-0 mt-3.5 flex items-center gap-3 bg-gradient-to-b from-sand/0 to-sand py-3.5 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={() => void store.goBack()}
          disabled={behaviorIndex === 0 && groupIndex === 0}
          className="cursor-pointer border-none bg-transparent px-1.5 py-3 font-mono text-xs tracking-[0.06em] text-soft uppercase disabled:cursor-default disabled:opacity-30"
        >
          ← {t('common.back')}
        </button>

        {/* The questionnaire moves by itself, so most of the time there is
            nothing here to press — and that is the point. The button used to
            sit enabled during the pause before the screen changed, which read
            as "nothing happened, press it again"; the second press landed on
            the screen after the one they meant to answer.

            It comes back exactly when nothing is going to move on its own: on
            the last screen, where stepping into the writing screen stays a
            deliberate act, and for somebody who went back and is reading
            through answers they have already given. */}
        {store.advancing ? (
          <span
            aria-live="polite"
            className="ml-auto flex max-w-[340px] flex-1 items-center justify-center gap-2 py-3.5 font-mono text-[11px] tracking-[0.1em] text-faint uppercase"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal" aria-hidden />
            {t('survey.advancing')}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => void store.goNext()}
            disabled={!screenComplete(store)}
            className="ml-auto max-w-[340px] flex-1 cursor-pointer rounded-[13px] border-none bg-teal px-4 py-3.5 text-[15px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:cursor-default disabled:bg-[#CDCAC2] disabled:shadow-none"
          >
            {isLastScreen
              ? t('survey.nextComments')
              : isLastGroupOfBehavior
                ? t('survey.nextBehavior')
                : t('survey.nextGroup')}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Anchors for the people on this screen.
 *
 * The API groups a screen by role level, so every person here reads the same
 * anchors and taking the first one's level is exact, not an approximation.
 */
const anchorsFor = (
  survey: NonNullable<ReturnType<typeof useSurveyStore.getState>['survey']>,
  behaviorId: string,
  people: { roleLevel: RoleLevel }[],
): AnchorDto[] => {
  const level = people[0]?.roleLevel;
  return (level && survey.anchorsByRoleLevel[level]?.[behaviorId]) ?? [];
};
